/* =========================================================================
   WMS · Доставка — суммы в сумах по курсу USD→UZS (SCRUM-585, бэк SCRUM-584,
   wms-currency-rate-scrum-585.md).

   Цены заказов из 1С в USD; строки API несут `currency_rate` («12850.0000»)
   — снимок маршрута или текущий курс. `null` — курса ещё нет ни разу: тогда
   показываем `$ 250`, а не неверную сумму в сумах.
   Поля, которые бэк уже отдаёт в сумах (наличные водителя, касса), сюда не
   ходят — их не умножаем.
   ========================================================================= */

/** «12850.5» → 128505n при places = 1: десятичная строка в целое со сдвигом. */
const scaled = (value: string, places: number): bigint => {
  const [whole, fraction = ''] = value.trim().split('.')
  return BigInt((whole || '0') + fraction.padEnd(places, '0').slice(0, places))
}

/**
 * `round_half_up(usd × rate)` до целого сума — как в PDF бэка. Только целые:
 * на float `4.35 * 12850` = 55897.4999… и округляется не туда.
 * usd — 2 знака, rate — 4, произведение сдвинуто на 10^6.
 */
export const toUzs = (usd: string, rate: string): bigint =>
  (scaled(usd, 2) * scaled(rate, 4) + BigInt(500000)) / BigInt(1000000)

/** 3212500n → «3 212 500 сум». */
export const formatUzs = (uzs: bigint | number, unit = 'сум'): string =>
  `${uzs.toLocaleString('ru-RU')} ${unit}`

/** «250.00» → «$ 250»; «10.25» → «$ 10,25». */
export const formatUsd = (usd: string | number): string =>
  `$ ${(Number(usd) || 0).toLocaleString('ru-RU', {maximumFractionDigits: 2})}`

/** «12850.0000» → «12 850», «12850.5000» → «12 850,5». */
export const formatRate = (rate: string): string =>
  (Number(rate) || 0).toLocaleString('ru-RU', {maximumFractionDigits: 4})

/** USD-сумма строки к показу: в сумах по курсу строки или `$ …` без курса. */
export const usdLabel = (
  usd: string | null | undefined,
  rate: string | null | undefined,
  unit = 'сум'
) => (rate ? formatUzs(toUzs(usd || '0', rate), unit) : formatUsd(usd || '0'))

/** USD-сумма → целые сумы числом (для итогов и сортировки); без курса — null. */
export const uzsOf = (
  usd: string | null | undefined,
  rate: string | null | undefined
): number | null => (rate ? Number(toUzs(usd || '0', rate)) : null)
